import { CircleHelp } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { SUPPORT_EMAIL } from '@/creator/pages/Faqs/faqContent';

const HelpMenu = () => {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuId = useId();

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const closeOnOutsidePointer = (event: PointerEvent): void => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('pointerdown', closeOnOutsidePointer);
        menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
    }, [isOpen]);

    const handleClose = (): void => setIsOpen(false);

    const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setIsOpen(false);
            triggerRef.current?.focus();
            return;
        }
        if (event.key === 'Tab') {
            setIsOpen(false);
            return;
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
            return;
        }
        event.preventDefault();
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
        const currentIndex = items.indexOf(document.activeElement as HTMLElement);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(currentIndex + step + items.length) % items.length]?.focus();
    };

    return (
        <div className={`help-menu ${isOpen ? 'is-open' : ''}`} ref={rootRef}>
            <button
                ref={triggerRef}
                type="button"
                className="help-menu__trigger"
                aria-label="Help"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                onClick={() => setIsOpen((current) => !current)}
            >
                <CircleHelp size={19} aria-hidden="true" />
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    id={menuId}
                    className="help-menu__list"
                    role="menu"
                    aria-label="Help"
                    onKeyDown={handleMenuKeyDown}
                >
                    <Link role="menuitem" to="/faqs" onClick={handleClose}>
                        FAQs
                    </Link>
                    <a role="menuitem" href={`mailto:${SUPPORT_EMAIL}`} onClick={handleClose}>
                        Contact
                    </a>
                </div>
            )}
        </div>
    );
};

export default HelpMenu;
